import * as React from "react";
import {
  Grid,
  TextField,
  InputLabel,
  Radio,
  Snackbar,
  Select,
  Box,
  Autocomplete,
  Chip,
} from "@mui/material";
import InputMask from "react-input-mask";
import globals, { LANGUAGE_OPTIONS } from "../../../../../globals/globals";
import { Alert } from "@mui/material";
import moment from "moment";

// helper
import { getHeaders } from "../../../../../utils/HeaderHelper";
import CustomDatePicker from "../../../../../common/CustomDatePicker";
import CloseIcon from "@mui/icons-material/Close";
import { MAX_ADDRESS_LENGTH, getAddressError, getAddressLine2Error, MAX_CITY_LENGTH, getCityError, MAX_NAME_LENGTH, getNameError, ZIP_LENGTH, sanitizeZip, getZipError, ZIP_EXT_LENGTH, sanitizeZipExtension, getZipExtensionError, MAX_PHONE_LENGTH, getPhoneNumberError, getEmailError, MAX_EMAIL_LENGTH } from "../../../../../utils/DemographicValidationHelper";
import { normalizeWhitespaceForSave } from "../../../../../utils/WhitespaceHelper";
import { useFlash } from "../../../../../FlashContext";

interface Props {
  csrfToken: string;
  selectedPatient: any;
  unsavedChanges: boolean;
  setUnsavedChanges: any;
  onUpdate: Function;
  triggerUpdate: boolean;
  setTriggerUpdate: Function;
  setRefreshPatientInfo: Function;
}

const PatientEdit: React.FC<Props> = (props) => {
  const { showToast } = useFlash();

  const normalizedZip = props.selectedPatient?.zip?.replace(/-/g, "") || "";

  const [firstName, setFirstName] = React.useState<string>(
    props.selectedPatient?.first_name || ""
  );
  const [middleName, setMiddleName] = React.useState<string>(
    props.selectedPatient?.middle_name || ""
  );
  const [lastName, setLastName] = React.useState<string>(
    props.selectedPatient?.last_name || ""
  );
  const [email, setEmail] = React.useState<string>(
    props.selectedPatient?.email || ""
  );
  const [address, setAddress] = React.useState<string>(
    props.selectedPatient?.address_line_1 || ""
  );
  const [addressLine2, setAddressLine2] = React.useState<string>(
    props.selectedPatient?.address_line_2 || ""
  );
  const [city, setCity] = React.useState<string>(
    props.selectedPatient?.city || ""
  );
  const [state, setState] = React.useState<string>(
    props.selectedPatient?.state || ""
  );
  const [zip, setZip] = React.useState<string>(
    normalizedZip ? normalizedZip.slice(0, 5) : ""
  );

  const [zipExtension, setZipExtension] = React.useState<string>(
    normalizedZip && normalizedZip.length === 9 ? normalizedZip.slice(5) : ""
  );
  const [county, setCounty] = React.useState<string>(
    props.selectedPatient?.county || ""
  );
  const [race, setRace] = React.useState<string>(
    props.selectedPatient?.race || ""
  );
  const [ethnicity, setEthnicity] = React.useState<string>(
    props.selectedPatient?.ethnicity || ""
  );
  const [mobilePhoneNumber, setMobilePhoneNumber] = React.useState<string>(
    props.selectedPatient?.mobile_phone_number || ""
  );
  const [dateOfBirth, setDateOfBirth] = React.useState<moment.Moment>(
    moment(props.selectedPatient?.date_of_birth) || moment()
  );
  const [gender, setGender] = React.useState<string>(
    props.selectedPatient?.gender || ""
  );
  const [error, setError] = React.useState<string>("");
  const [invalidFieldsArray, setInvalidFieldsArray] = React.useState<string[]>(
    []
  );
  const [homePhoneNumber, setHomePhoneNumber] = React.useState<string>(
    props.selectedPatient?.home_phone_number || ""
  );
  const [contactChannel, setContactChannel] = React.useState<string>(
    props.selectedPatient?.contact_channel || "mobile_phone"
  );

  const [contactMethod, setContactMethod] = React.useState<string>(
    props.selectedPatient?.contact_method || "call"
  );

  const [languages, setLanguages] = React.useState<string[]>(
    props.selectedPatient?.languages || []
  );

  const [employerName, setEmployerName] = React.useState<string>(
    props.selectedPatient?.employer_name || ""
  );

  const raceOptions = ["Hispanic or Latino", "Not Hispanic or Latino"];
  const ethnicityOptions = [
    "White",
    "Black or African American",
    "American Indian or Alaskan Native",
    "Asian",
    "Native Hawaiian or Other Pacific Islander",
  ];

  React.useEffect(() => {
    if (props.triggerUpdate) {
      updatePatient();
    }
  }, [props.triggerUpdate]);

  const handleDateChange = (date) => {
    setDateOfBirth(date);
    props.setUnsavedChanges(true);
  };

  const stateOptions = globals.states.map((state) => {
    return (
      <option key={state} value={state}>
        {state}
      </option>
    );
  });

  const validForm = () => {
    let valid = false;
    const fields = [
      [firstName, "First Name"],
      [lastName, "Last Name"],
      [email, "Email Address"],
      [address, "Address"],
      [city, "City"],
      [state, "State"],
      [zip, "Zip"],
      [gender, "Gender"],
    ];
    const invalid = fields.filter((field) => !field[0] || field[0].length == 0);
    let invalidFieldArrayObject = invalid.map((field) => field[1]);

    if (invalidFieldArrayObject.length == 0) {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      if (!re.test(String(email).toLowerCase())) {
        let invalidFields = invalidFieldArrayObject;
        invalidFields.push("a valid Email Address");
        setInvalidFieldsArray(invalidFields);
        valid = false;
      } else {
        valid = true;
      }
    } else {
      setInvalidFieldsArray(invalidFieldArrayObject);
    }
    return valid;
  };

  const validZipCode = () => {
  setError("");

  const sanitizedZip = zip.trim();
  const sanitizedZipExtension = zipExtension ? zipExtension.trim() : "";

  if (!/^\d{5}$/.test(sanitizedZip)) {
    const message = "ZIP Code must be exactly 5 digits.";
    setError(message);
    showToast({ title: "Invalid ZIP Code", text: message, type: "error" });
    return false;
  }

  if (sanitizedZipExtension && !/^\d{4}$/.test(sanitizedZipExtension)) {
    const message = "ZIP Extension must be exactly 4 digits.";
    setError(message);
    showToast({ title: "Invalid ZIP Extension", text: message, type: "error" });
    return false;
  }

  return true;
};

  React.useEffect(() => {
    if (invalidFieldsArray.length > 0) {
      const message = `Please fill out ${invalidFieldsArray.join(", ")}`;
      setError(message);
      showToast({ title: "Patient Info Required Field Missing", text: message, type: "error" });
      props.setTriggerUpdate(false);
    }
  }, [invalidFieldsArray]);

  const updatePatient = () => {
    setError("");

    if (!validForm()) {
      setError("Invalid entries, please check your entries and try again.");
      props.setTriggerUpdate(false);
      return;
    }

    if (!validZipCode()) {
      props.setTriggerUpdate(false);
      return;
    }

    // Build zip code cleanly
    const sanitizedZip = zip.trim();
    const sanitizedZipExtension = zipExtension?.trim() || "";
    const zipCode = sanitizedZipExtension.length === 4 ? sanitizedZip + sanitizedZipExtension : zip;

    // Build payload
    const payload = {
      user: {
        id: props.selectedPatient.id,
        first_name: normalizeWhitespaceForSave(firstName),
        middle_name: normalizeWhitespaceForSave(middleName),
        last_name: normalizeWhitespaceForSave(lastName),
        email: normalizeWhitespaceForSave(email),
        address_line_1: normalizeWhitespaceForSave(address),
        address_line_2: normalizeWhitespaceForSave(addressLine2),
        city: normalizeWhitespaceForSave(city),
        state: normalizeWhitespaceForSave(state),
        zip: zipCode,
        county: normalizeWhitespaceForSave(county),
        race: race,
        ethnicity: ethnicity,
        mobile_phone_number: normalizeWhitespaceForSave(mobilePhoneNumber),
        gender: gender,
        date_of_birth: dateOfBirth.format("YYYY-MM-DD"),
        home_phone_number: normalizeWhitespaceForSave(homePhoneNumber),
        contact_channel: contactChannel,
        contact_method: contactMethod,
        languages: languages,
        employer_name: normalizeWhitespaceForSave(employerName),
      },
    };

    fetch("/edit_patient_info", {
      method: "POST",
      headers: getHeaders(props.csrfToken),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.error) {
          const message = typeof result.error === "string" ? result.error : "Unable to save patient info.";
          setError(message);
          showToast({ title: "Unable to Save Patient Info", text: message, type: "error" });
          props.setTriggerUpdate(false);
          return;
        }

        showToast({
          title: "Patient Updated Successfully",
          text: "You have successfully updated the patient",
          type: "success",
        });

        props.onUpdate();
        props.setRefreshPatientInfo(true);
      })
      .catch((error) => {
        setError(error);
        showToast({
          title: "Unable to Save Patient Info",
          text: "Something went wrong while saving. Please try again.",
          type: "error",
        });
        props.setTriggerUpdate(false);
      });
  };

  return (
    <Grid item xs={12} className="patient-edit-container patient-edit-form">
      <Grid container>
        <Grid container className="form-container">
          <Grid item xs={2.5} sx={{ mr: 5 }}>
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="last_name" className="field-label">
                  Last Name*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                 <TextField
                  id="last_name"
                  size="small"
                  value={lastName}
                  className="the-field"
                  required
                  variant="outlined"
                  error={Boolean(getNameError(lastName, "Last Name"))}
                  helperText={getNameError(lastName, "Last Name")}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: MAX_NAME_LENGTH }}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="first_name" className="field-label">
                  First Name*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="first_name"
                  size="small"
                  value={firstName}
                  className="the-field"
                  required
                  variant="outlined"
                  error={Boolean(getNameError(firstName, "First Name"))}
                  helperText={getNameError(firstName, "First Name")}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: MAX_NAME_LENGTH }}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="middle_name" className="field-label">
                  Middle Name
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="middle_name"
                  size="small"
                  value={middleName}
                  className="the-field"
                  variant="outlined"
                  error={Boolean(getNameError(middleName, "Middle Name"))}
                  helperText={getNameError(middleName, "Middle Name")}
                  onChange={(event) => {
                    setMiddleName(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: MAX_NAME_LENGTH }}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="payer" className="field-label">
                  Payer
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="payer"
                  size="small"
                  variant="outlined"
                  value={props.selectedPatient?.payer || "-"}
                  className="the-field"
                  slotProps={{ input: { readOnly: true } }}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="employer_name" className="field-label">
                  Employer Name
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="employer_name"
                  size="small"
                  value={employerName}
                  className="the-field"
                  variant="outlined"
                  placeholder="Enter Employer Name"
                  onChange={(event) => {
                    setEmployerName(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            </Grid>

          </Grid>

          <Grid item xs={2.5} sx={{ mr: 5 }}>
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="address" className="field-label">
                  Address Line One*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="address"
                  size="small"
                  value={address}
                  required
                  variant="outlined"
                  className="the-field"
                  error={getAddressError(address).error}
                  helperText={getAddressError(address).helperText}
                  onChange={(event) => {
                    setAddress(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: MAX_ADDRESS_LENGTH }}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="addressLine2" className="field-label">
                  Address Line Two
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="addressLine2"
                  size="small"
                  value={addressLine2}
                  variant="outlined"
                  className="the-field"
                  error={getAddressLine2Error(addressLine2).error}
                  helperText={getAddressLine2Error(addressLine2).helperText}
                  onChange={(event) => {
                    setAddressLine2(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: MAX_ADDRESS_LENGTH }}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="city" className="field-label">
                  City*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="city"
                  size="small"
                  value={city}
                  required
                  variant="outlined"
                  className="the-field"
                  error={getCityError(city).error}
                  helperText={getCityError(city).helperText}
                  onChange={(event) => {
                    setCity(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  inputProps={{ maxLength: MAX_CITY_LENGTH }}
                />
              </Grid>
            </Grid>

            <Grid container justifyContent="space-between">
              <Grid container xs={5.5}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="state" className="field-label">
                    State*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Select
                    id="state"
                    value={state}
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(event) => {
                      setState(event.target.value);
                      props.setUnsavedChanges(true);
                    }}
                    native
                  >
                    <option value={""}>Select</option>
                    {stateOptions}
                  </Select>
                </Grid>
              </Grid>
              <Grid container xs={5.5} sx={{ mr: 1 }}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="county" className="field-label">
                    County
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="county"
                    size="small"
                    value={county}
                    placeholder="Enter County"
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(event) => {
                      setCounty(event.target.value);
                      props.setUnsavedChanges(true);
                    }}
                    inputProps={{ maxLength: 50 }}
                    error={county.length > 50}
                    helperText={county.length > 50 ? "Maximum 50 characters allowed." : ""}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container justifyContent="space-between">
              <Grid container xs={5}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="zip" className="field-label">
                    ZIP Code*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="zip"
                    placeholder="Enter Zip"
                    size="small"
                    value={zip}
                    required
                    variant="outlined"
                    className="the-field"
                    onChange={(event) => {
                      const sanitized = sanitizeZip(event.target.value);
                      setZip(sanitized);
                      props.setUnsavedChanges(true);
                    }}
                    onKeyPress={(event) => {
                      // props.setUnsavedChanges(true);
                    }}
                    slotProps={{
                      htmlInput: { inputMode: "numeric", maxLength: ZIP_LENGTH },
                    }}
                    error={getZipError(zip).error}
                    helperText={getZipError(zip).helperText}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                xs="auto"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "-2%",
                }}
              >
                <span
                  style={{
                    fontSize: "1.5rem",
                    marginTop: "45px",
                    color: "grey",
                  }}
                >
                  –
                </span>
              </Grid>
              <Grid container xs={5} sx={{ mr: 1 }}>
                <Grid item xs={12}>
                  <InputLabel htmlFor="zipExtension" className="field-label">
                    Extension
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="zipExtension"
                    placeholder="Enter Extension"
                    size="small"
                    value={zipExtension}
                    variant="outlined"
                    className="the-field"
                    onChange={(event) => {
                      const sanitized = sanitizeZipExtension(event.target.value);
                      setZipExtension(sanitized);
                      props.setUnsavedChanges(true);
                    }}
                    onKeyPress={(event) => {
                      // props.setUnsavedChanges(true);
                    }}
                    slotProps={{
                      htmlInput: { inputMode: "numeric", maxLength: ZIP_EXT_LENGTH },
                    }}
                    error={getZipExtensionError(zipExtension).error}
                    helperText={getZipExtensionError(zipExtension).helperText}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={2.5} sx={{ mr: 5 }}>
            <CustomDatePicker
              id={"date_of_birth"}
              label={"Date Of Birth"}
              placeholder={"Select Birthdate"}
              date={dateOfBirth}
              handleDateChange={handleDateChange}
              disabled={false}
            />
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="state" className="field-label">
                  Ethnicity
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <Select
                  id="ethnicity"
                  value={ethnicity}
                  required
                  variant="outlined"
                  className="the-field"
                  onChange={(event) => {
                    setEthnicity(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  native
                >
                  <option value={""}>Select Ethnicity</option>
                  {ethnicityOptions.map((eth, index) => (
                    <option key={index} value={eth}>
                      {eth}
                    </option>
                  ))}
                </Select>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="state" className="field-label">
                  Race
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <Select
                  id="race"
                  value={race}
                  required
                  variant="outlined"
                  className="the-field"
                  onChange={(event) => {
                    setRace(event.target.value);
                    props.setUnsavedChanges(true);
                  }}
                  native
                >
                  <option value={""}>Select Race</option>
                  {raceOptions.map((r, index) => (
                    <option key={index} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="languages" className="field-label">
                  Language(s)
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <Autocomplete
                  multiple
                  id="languages"
                  options={LANGUAGE_OPTIONS}
                  value={languages}
                  onChange={(_, newValue) => {
                    setLanguages(newValue);
                    props.setUnsavedChanges(true);
                  }}
                  disableClearable
                  filterSelectedOptions
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        deleteIcon={
                          <CloseIcon sx={{ color: "white !important" }} />
                        }
                        sx={{
                          backgroundColor: "#AAA3A0",
                          color: "#FFFFFF",
                          margin: "5px !important",
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select languages"
                      size="small"
                      className="the-field"
                      sx={{
                        "& .MuiInputBase-root": {
                          minHeight: "42px",
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                      paddingTop: "0px",
                      paddingBottom: languages.length < 1 && "0px",
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container className="field-container">
              <Grid item xs={12}>
                <InputLabel htmlFor="gender" className="field-label">
                  Sex
                </InputLabel>
              </Grid>
              <Grid item xs={12}>
                <div className="row" style={{ height: 40 }}>
                  <Radio
                    checked={gender === "Male"}
                    onChange={(event) => {
                      setGender(event.target.value);
                      props.setUnsavedChanges(true);
                    }}
                    value="Male"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "Male" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label">Male</p>
                  <Radio
                    checked={gender === "Female"}
                    onChange={(event) => {
                      setGender(event.target.value);
                      props.setUnsavedChanges(true);
                    }}
                    value="Female"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "Female" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                  />
                  <p className="radio-label">Female</p>
                  <Radio
                    checked={gender === "Unknown"}
                    onChange={(event) => {
                      setGender(event.target.value);
                      props.setUnsavedChanges(true);
                    }}
                    value="Unknown"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "Unknown" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 2, width: 32, height: 32 }}
                  />
                  <p className="radio-label">Unknown</p>
                </div>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={2.5} sx={{ mr: 5 }}>
            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="email" className="field-label">
                  Email Address*
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <TextField
                  id="email-address"
                  size="small"
                  variant="outlined"
                  className="the-field"
                  inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
                  helperText={getEmailError(email)}
                  error={!!getEmailError(email)}
                  required
                  value={email}
                  type="email"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel
                  htmlFor="mobile_phone_number"
                  className="field-label"
                >
                  Mobile Phone Number
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <InputMask
                  id="mobile-phone-number"
                  mask="(999)-999-9999"
                  value={mobilePhoneNumber}
                  disabled={false}
                  maskChar=""
                  variant="outlined"
                  className="the-field"
                  size="small"
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);
                    setMobilePhoneNumber(numericValue);
                    props.setUnsavedChanges(true);
                  }}
                >
                  {() => (
                    <TextField
                      variant="outlined"
                      size="small"
                      className="the-field"
                      error={getPhoneNumberError(mobilePhoneNumber).error}
                      helperText={getPhoneNumberError(mobilePhoneNumber).helperText}
                    />
                  )}
                </InputMask>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="homePhoneNumber" className="field-label">
                  Home Phone Number
                </InputLabel>
              </Grid>
              <Grid item xs={12} className="field-container">
                <InputMask
                  id="homePhoneNumber"
                  mask="(999)-999-9999"
                  value={homePhoneNumber}
                  disabled={false}
                  maskChar=""
                  variant="outlined"
                  className="the-field"
                  size="small"
                  onChange={(event) => {
                    const numericValue = event.target.value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);
                    setHomePhoneNumber(numericValue);
                    props.setUnsavedChanges(true);
                  }}
                >
                  {() => (
                    <TextField
                      variant="outlined"
                      size="small"
                      className="the-field"
                      error={getPhoneNumberError(homePhoneNumber).error}
                      helperText={getPhoneNumberError(homePhoneNumber).helperText}
                    />
                  )}
                </InputMask>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="contactChannel" className="field-label">
                  Preferred Contact Channel
                </InputLabel>
              </Grid>
              <Box>
                <div className="row" style={{ height: 40 }}>
                  <Radio
                    checked={contactChannel === "mobile_phone"}
                    onChange={(event) => {
                      setContactChannel(event.target.value);
                      if (props.selectedPatient?.contact_method) {
                        setContactMethod(props.selectedPatient?.contact_method);
                      } else {
                        setContactMethod("call");
                      }
                      props.setUnsavedChanges(true);
                    }}
                    value="mobile_phone"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "mobile_phone" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                    Mobile Phone
                  </p>
                  <Radio
                    checked={contactChannel === "home_phone"}
                    onChange={(event) => {
                      setContactChannel(event.target.value);
                      setContactMethod("call");
                      props.setUnsavedChanges(true);
                    }}
                    value="home_phone"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "home_phone" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                    Home Phone
                  </p>
                  <Radio
                    checked={contactChannel === "email"}
                    onChange={(event) => {
                      setContactChannel(event.target.value);
                      setContactMethod("email");
                      props.setUnsavedChanges(true);
                    }}
                    value="email"
                    name="radio-buttons"
                    inputProps={{ "aria-label": "email" }}
                    style={{ backgroundColor: "transparent" }}
                    sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                  />
                  <p className="radio-label">Email</p>
                </div>
              </Box>
            </Grid>

            <Grid container>
              <Grid item xs={12}>
                <InputLabel htmlFor="contactMethod" className="field-label">
                  Preferred Contact Method
                </InputLabel>
              </Grid>
              <Box>
                {contactChannel === "mobile_phone" ? (
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={contactMethod === "call"}
                      onChange={(event) => {
                        setContactMethod(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      value="call"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "call" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                      Call
                    </p>

                    <Radio
                      checked={contactMethod === "text"}
                      onChange={(event) => {
                        setContactMethod(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      value="text"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "text" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                      Text
                    </p>
                  </div>
                ) : contactChannel === "home_phone" ? (
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={contactMethod === "call"}
                      onChange={(event) => {
                        setContactMethod(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      value="call"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "call" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label" style={{ whiteSpace: "nowrap" }}>
                      Call
                    </p>
                  </div>
                ) : contactChannel === "email" ? (
                  <div className="row" style={{ height: 40 }}>
                    <Radio
                      checked={contactMethod === "email"}
                      onChange={(event) => {
                        setContactMethod(event.target.value);
                        props.setUnsavedChanges(true);
                      }}
                      value="email"
                      name="radio-buttons"
                      inputProps={{ "aria-label": "email" }}
                      style={{ backgroundColor: "transparent" }}
                      sx={{ color: "#A29D9B", ml: 1, width: 32, height: 32 }}
                    />
                    <p className="radio-label">Email</p>
                  </div>
                ) : (
                  <></>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PatientEdit;
